/**
 * THE standard for showing dates and times anywhere in the web app.
 * Always Australia/Sydney (AEST/AEDT, daylight saving applied), regardless of the viewer's computer.
 *
 *   formatSydneyDate(v)         -> 13 Oct 2026
 *   formatSydneyDateTime(v)     -> 13 Oct 2026, 14:05        (24-hour)
 *   formatSydneyDateTimeSec(v)  -> 13 Oct 2026, 14:05:09     (logs / transactions)
 *   formatSydneyDateTimeMs(v)   -> 13 Oct 2026, 14:05:09.123 (transaction timestamps)
 *   formatSydneyDateTimeAuto(v) -> date only when there is no real time of day, else date + time
 *   formatSydneyTime(v)         -> 14:05
 *
 * Do not call toLocaleDateString / toLocaleTimeString / toLocaleString on dates in UI code.
 * Date-only strings ("2026-10-13", trading days, DOBs) are shown as that calendar day, never shifted.
 */

export const SYDNEY_TZ = 'Australia/Sydney'

/** Newer ICU spells September as "Sept"; keep the standard three-letter month everywhere. */
function fixMonth(str) {
  return String(str).replace(/\bSept\b/g, 'Sep')
}

/**
 * @param {string|number|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [opts]
 * @returns {string}
 */
export function formatSydneyDateTime(value, opts = {}) {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) {
    // Legacy non-ISO display string — show as-is
    if (typeof value === 'string' && value.trim()) return value.trim()
    return '—'
  }
  const hasStyle = opts.dateStyle != null || opts.timeStyle != null
  try {
    return fixMonth(d.toLocaleString('en-AU', {
      timeZone: SYDNEY_TZ,
      ...(hasStyle
        ? {}
        : { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
      ...opts,
    }))
  } catch {
    return d.toISOString()
  }
}

/**
 * Date only in Sydney calendar (for instants that should show the Sydney day).
 * @param {string|number|Date|null|undefined} value
 */
export function formatSydneyDate(value, opts = {}) {
  if (value == null || value === '') return '—'
  // Already a plain calendar date — do not shift via UTC midnight
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T00:00:00(\.0+)?Z?)?$/.test(value.trim())) {
    const [y, m, day] = value.trim().slice(0, 10).split('-').map(Number)
    const d = new Date(Date.UTC(y, m - 1, day, 12, 0, 0))
    try {
      return fixMonth(d.toLocaleDateString('en-AU', {
        timeZone: SYDNEY_TZ,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...opts,
      }))
    } catch {
      return value.trim()
    }
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '—'
  try {
    return fixMonth(d.toLocaleDateString('en-AU', {
      timeZone: SYDNEY_TZ,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      ...opts,
    }))
  } catch {
    return '—'
  }
}

/**
 * Time-only portion in Sydney (e.g. end of a range after a full datetime).
 */
export function formatSydneyTime(value, opts = {}) {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  try {
    return d.toLocaleTimeString('en-AU', {
      timeZone: SYDNEY_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      ...opts,
    })
  } catch {
    return '—'
  }
}

/**
 * Sydney wall clock (DST-aware) for <input type="datetime-local">: "YYYY-MM-DDTHH:mm".
 * @param {string|number|Date} value instant
 */
export function toSydneyDatetimeLocal(value) {
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const parts = {}
  for (const p of new Intl.DateTimeFormat('en-CA', {
    timeZone: SYDNEY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d)) {
    parts[p.type] = p.value
  }
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

/**
 * Convert a Sydney wall-clock "YYYY-MM-DDTHH:mm[:ss]" (no zone) to the real instant,
 * applying AEST (+10) or AEDT (+11) for that date. Returns null if not parseable.
 * @param {string} wall
 * @returns {Date|null}
 */
export function sydneyWallClockToDate(wall) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(wall || '').trim())
  if (!m) return null
  const [y, mo, da, h, mi, s] = m.slice(1).map((x) => (x == null ? 0 : Number(x)))
  const asUtc = Date.UTC(y, mo - 1, da, h, mi, s)
  // Offset of Sydney from UTC at (approximately) that instant; re-check once to settle DST edges.
  const offsetAt = (ms) => {
    const local = toSydneyDatetimeLocal(new Date(ms))
    const lm = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local)
    return Date.UTC(Number(lm[1]), Number(lm[2]) - 1, Number(lm[3]), Number(lm[4]), Number(lm[5])) - Math.floor(ms / 60000) * 60000
  }
  let guess = asUtc - offsetAt(asUtc)
  guess = asUtc - offsetAt(guess)
  return new Date(guess)
}

/** Date + time with seconds, e.g. "13 Oct 2026, 14:05:09". */
export function formatSydneyDateTimeSec(value) {
  return formatSydneyDateTime(value, { second: '2-digit' })
}

/** Date + time with milliseconds, e.g. "13 Oct 2026, 14:05:09.123". */
export function formatSydneyDateTimeMs(value) {
  return formatSydneyDateTime(value, { second: '2-digit', fractionalSecondDigits: 3 })
}

/**
 * Date only when the value has no real time of day (date column, or midnight UTC placeholder);
 * otherwise date + time. Use for fields that can hold either (DOB, expiry, "last updated").
 */
export function formatSydneyDateTimeAuto(value) {
  if (value == null || value === '') return '—'
  const s = typeof value === 'string' ? value.trim() : null
  if (s && (/^\d{4}-\d{2}-\d{2}$/.test(s) || /^\d{4}-\d{2}-\d{2}[T ]00:00(:00(\.0+)?)?Z?$/.test(s))) {
    return formatSydneyDate(s.slice(0, 10))
  }
  return formatSydneyDateTime(value)
}

/** Sydney calendar day for an instant (default now), as "YYYY-MM-DD". */
export function sydneyYmd(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return toSydneyDatetimeLocal(d).slice(0, 10)
}

/**
 * A Date whose *local* getters (getFullYear/getMonth/getDate/getHours...) show the current Sydney wall clock.
 * Use only for calendar maths ("this month", "today") — never to format or send an instant.
 */
export function sydneyNowAsLocalDate(value = new Date()) {
  const wall = toSydneyDatetimeLocal(value instanceof Date ? value : new Date(value))
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall)
  if (!m) return new Date()
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]))
}
